// Customer Orders — full-screen view of ONE customer's entire relationship with
// the company: every inquiry/order they've ever had (a single customer can
// accumulate hundreds), each with the same detail + approval flow as the Orders
// list. Opened from the Orders screen instead of a cramped inline dropdown.

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  FiArrowLeft,
  FiRefreshCw,
  FiPhone,
  FiMapPin,
  FiShoppingCart,
  FiAlertCircle,
} from 'react-icons/fi';
import MainLayout from '../components/layout/MainLayout.jsx';
import { Alert, DeleteConfirm } from '../components/common/index.js';
import axiosInstance from '../api/axiosInstance';
import { API_ENDPOINTS } from '../config/api.config';
import { deleteOrder } from '../api/deletionsApi';
import { fetchInquiryDetail, clearDetail } from '../redux/slices/inquirySlice';
import { OrderCard, ApprovalModal, DetailModal } from './OrdersPage.jsx';

const formatPrice = (value) => {
  if (value == null || value === '') return '₹0';
  const n = Number(value);
  if (!Number.isFinite(n)) return '₹0';
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const REVIEW_STYLE = {
  warn: 'border-amber-200 bg-amber-50 text-amber-700',
  info: 'border-sky-200 bg-sky-50 text-sky-700',
  ok: 'border-emerald-200 bg-emerald-50 text-emerald-700',
};

const CustomerOrdersPage = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { detail, detailLoading, detailError } = useSelector((s) => s.inquiries);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.ORDERS.CUSTOMER(customerId));
      setData(res.data ?? res);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not load this customer');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  // A won-but-not-delivered order (confirmed) opens the approve-payment-&-dispatch
  // flow; any other order just shows its detail.
  const openOrder = (order) => {
    if (order.status === 'confirmed') {
      setApproveTarget(order);
      return;
    }
    setModalOpen(true);
    dispatch(fetchInquiryDetail(order.inquiry_id));
  };
  const closeDetail = () => {
    setModalOpen(false);
    dispatch(clearDetail());
  };

  const submitDelete = async ({ password, reason }) => {
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await deleteOrder(deleteTarget.inquiry_id, { password, reason, section: 'orders' });
      setDeleteTarget(null);
      await load();
    } catch (e) {
      setDeleteError(e?.response?.data?.error || e.message || 'Could not delete the order');
    } finally {
      setDeleteBusy(false);
    }
  };

  const customer = data?.customer;
  const orders = data?.orders ?? [];
  const review = data?.review;
  const rev = REVIEW_STYLE[review?.level] || REVIEW_STYLE.ok;

  return (
    <MainLayout>
      <div className="space-y-5 pb-8">
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/orders')}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 shadow-sm transition-all hover:border-violet-300 hover:bg-violet-50 hover:text-violet-600"
          >
            <FiArrowLeft className="h-4 w-4" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-lg font-bold text-slate-900 sm:text-xl">
              {customer ? customer.name : 'Customer'}
            </h1>
            <p className="flex items-center gap-2 truncate text-sm text-slate-500">
              {customer ? (
                <>
                  <span className="inline-flex items-center gap-1"><FiPhone className="h-3 w-3" /> {customer.phone || '—'}</span>
                  {customer.location && <span className="inline-flex items-center gap-1"><FiMapPin className="h-3 w-3" /> {customer.location}</span>}
                </>
              ) : (loading ? 'Loading…' : ' ')}
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            title="Refresh"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-400 shadow-sm transition-all hover:border-violet-300 hover:bg-violet-50 hover:text-violet-600 disabled:opacity-50"
          >
            <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {error && <Alert type="error" message={error} onClose={() => setError('')} />}

        {/* ── Summary ────────────────────────────────────────────────────── */}
        {data && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Revenue</p>
              <p className="mt-0.5 text-lg font-black text-slate-900">{formatPrice(data.revenue)}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Orders</p>
              <p className="mt-0.5 text-lg font-black text-slate-900">{data.orders_count}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Delivered</p>
              <p className="mt-0.5 text-lg font-black text-emerald-600">{data.delivered}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">To action</p>
              <p className="mt-0.5 text-lg font-black text-amber-600">{(data.awaiting_payment ?? 0) + (data.partial ?? 0) + (data.ready ?? 0)}</p>
            </div>
          </div>
        )}

        {review?.label && (
          <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold ${rev}`}>
            {review.level === 'warn' && <FiAlertCircle className="h-3.5 w-3.5" />}
            {review.label}
          </span>
        )}

        {/* ── Orders ─────────────────────────────────────────────────────── */}
        {loading ? (
          <div className="py-16 text-center text-sm text-slate-400">Loading…</div>
        ) : orders.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-14 text-center">
            <FiShoppingCart className="mx-auto mb-2 h-8 w-8 text-slate-200" />
            <p className="text-sm text-slate-400">No inquiries for this customer yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              All inquiries &amp; orders · newest first
            </p>
            {orders.map((order) => (
              <OrderCard
                key={order.inquiry_id}
                order={order}
                funnel
                onOpen={openOrder}
                onDelete={(o) => { setDeleteError(''); setDeleteTarget(o); }}
              />
            ))}
          </div>
        )}
      </div>

      {modalOpen && (
        <DetailModal detail={detail} loading={detailLoading} error={detailError} onClose={closeDetail} />
      )}

      {approveTarget && (
        <ApprovalModal
          order={approveTarget}
          onClose={() => setApproveTarget(null)}
          onApproved={() => { setApproveTarget(null); load(); }}
        />
      )}

      {deleteTarget && (
        <DeleteConfirm
          title={`Delete order #${deleteTarget.inquiry_id}?`}
          summary={`${deleteTarget.customer_name || customer?.name || 'Customer'} · ${deleteTarget.car || ''}`}
          consequences={[
            'The WHOLE order goes — its parts, chat, follow-ups and returns with it.',
            'Any stock-recovery entries its dispatch created are reversed.',
            'This cannot be undone (the snapshot in Deleted Records is read-only).',
          ]}
          requirePassword
          busy={deleteBusy}
          error={deleteError}
          onClose={() => { if (!deleteBusy) { setDeleteTarget(null); setDeleteError(''); } }}
          onConfirm={submitDelete}
        />
      )}
    </MainLayout>
  );
};

export default CustomerOrdersPage;
